/**
 * Investors Relations Contact Page (English)
 * /en/investors-relations-contact/
 */
import React from 'react'
import { graphql, PageProps } from 'gatsby'
import styled from '@emotion/styled'
import BlockContent from '@sanity/block-content-to-react'
import { FiMail, FiMapPin, FiSmartphone } from 'react-icons/fi'
import Layout from '../../components/Layout'
import HeroBanner from '../../components/HeroBanner'
import SEO from '../../components/SEO'
import RecordList from '../../components/RecordList'
import { IRContact } from '../../types'

const ContactGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  margin: 4rem 0;

  @media (max-width: 767px) {
    margin: 3rem 0 4rem;
  }

  @media (max-width: 360px) {
    flex-direction: column;
    margin: 2rem 0 1rem;
    gap: 2.5rem;
  }
`

const ContactCard = styled.div`
  width: calc(100% / 3);
  margin: 2rem 0;
  padding: 0 4rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  text-align: center;

  span {
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  @media (max-width: 767px) {
    width: calc(100% / 3);
    margin: 0;
    padding: 0 0.25rem;
    font-size: 12px;
    line-height: 1.35;

    svg {
      width: 58px;
      height: 58px;
    }
  }

  @media (max-width: 360px) {
    width: 100%;
    padding: 0;
  }
`

const ContactLabel = styled.span`
  font-size: 1.5rem;
  margin: 1rem 0 0.5rem;

  @media (max-width: 767px) {
    font-size: 1rem;
    margin-top: 0.75rem;
  }
`

const RegistrarSection = styled.section`
  width: 80%;
  max-width: 1024px;
  margin: 2rem auto 5rem;
  min-height: 20vh;

  @media (max-width: 767px) {
    width: 100%;
    margin: 2rem auto 4rem;
  }
`

const RegistrarTab = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  min-width: 112px;
  height: 45px;
  padding: 0 16px;
  border-radius: 4px 4px 0 0;
  background: ${({ color }) => color};
  color: #fff;
  font-size: 16px;
  font-weight: 500;
`

const RegistrarRule = styled.div<{ color: string }>`
  height: 1px;
  background: ${({ color }) => color};
`

const RegistrarBody = styled.div`
  padding: 16px 8px 0;
  color: #000;
  overflow-wrap: anywhere;

  @media (max-width: 767px) {
    padding: 16px 0 0;
  }
`

const RegistrarName = styled.h2`
  margin: 0 0 24px;
  font-size: 28px;
  line-height: 1.25;
  font-weight: 700;

  @media (max-width: 767px) {
    margin-bottom: 18px;
    font-size: 22px;
  }
`

const RegistrarText = styled.p`
  margin: 0 0 28px;
  font-size: 14px;
  line-height: 1.8;

  @media (max-width: 767px) {
    margin-bottom: 20px;
  }
`

const blockSerializers = {
  types: {
    block: ({ node, children }) => {
      if (node.style === 'h1') {
        return <RegistrarName>{children}</RegistrarName>
      }

      return <RegistrarText>{children}</RegistrarText>
    },
  },
}

export const query = graphql`
  query IRContactEnQuery {
    contact: sanityIrContact {
      id
      phone {
        zhHant
        en
      }
      email {
        zhHant
        en
      }
      address {
        zhHant
        en
      }
      _rawContent
    }
    sanitySiteSettings {
      color {
        hex
      }
    }
  }
`

interface IRContactEnPageProps extends PageProps {
  data: {
    contact: IRContact
    sanitySiteSettings: {
      color: { hex: string }
    }
  }
}

const LANG_KEY = 'en' as const
const LANG_FIELD = 'en' as const
const PAGE_TITLE = 'Investors Relations Contact'

const IRContactEnPage: React.FC<IRContactEnPageProps> = ({ data, location }) => {
  const { contact, sanitySiteSettings } = data
  const primaryColor = sanitySiteSettings?.color?.hex || '#ff6637'

  if (!contact) {
    return (
      <Layout langKey={LANG_KEY} currentPath={location.pathname}>
        <SEO title={PAGE_TITLE} lang="en" />
        <HeroBanner />
        <p style={{ padding: '2rem' }}>No contact information available</p>
      </Layout>
    )
  }

  const { phone, email, address, _rawContent } = contact
  const registrarContent = _rawContent?.[LANG_FIELD]

  return (
    <Layout langKey={LANG_KEY} fluid={true} currentPath={location.pathname}>
      <SEO title={PAGE_TITLE} lang="en" />
      <HeroBanner />

      <RecordList>
        <ContactGrid>
          <ContactCard>
            <FiSmartphone size={108} color={primaryColor} />
            <ContactLabel>Phone</ContactLabel>
            <span>{phone?.[LANG_FIELD]}</span>
          </ContactCard>
          <ContactCard>
            <FiMail size={108} color={primaryColor} />
            <ContactLabel>E-mail</ContactLabel>
            <span>{email?.[LANG_FIELD]}</span>
          </ContactCard>
          <ContactCard>
            <FiMapPin size={108} color={primaryColor} />
            <ContactLabel>Office</ContactLabel>
            <span>{address?.[LANG_FIELD]}</span>
          </ContactCard>
        </ContactGrid>

        <RegistrarSection>
          <RegistrarTab color={primaryColor}>Securities Registrar</RegistrarTab>
          <RegistrarRule color={primaryColor} />
          <RegistrarBody>
            {registrarContent?.length ? (
              <BlockContent blocks={registrarContent} serializers={blockSerializers} />
            ) : null}
          </RegistrarBody>
        </RegistrarSection>
      </RecordList>
    </Layout>
  )
}

export default IRContactEnPage

